import {
    Box, CircularProgress, Alert, Typography, Button,
    Paper, AppBar, Toolbar, Tab, Tabs,
    Grid,
} from "@mui/material";
import { useState, useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { getActiveTaxonomySchemes, getCustomObjectTaxonomies, updateTaxonomy, updateTaxonomyConfiguration } from "../../api/taxonomies";
import { AlertMessage, ApiDetailedTaxonomy, ApiSchemes } from "../../api/types";
import { useGetQuery } from "../../hooks/useGetQuery";
import { ContainerWrapper } from "../containerWrapper";
import { Loader } from "../loader";
import { theme } from "../../helpers/theme";
import { TaxonomyDetails } from "./subTaxonomyPages/taxonomyDetails";
import { TaxonomyEntryPoints } from "./subTaxonomyPages/taxonomyEntryPoints";
import { TaxonomyElements } from "./subTaxonomyPages/taxonomyElements";
import { TaxonomyDimensionValues } from "./subTaxonomyPages/taxonomyDimensionValues";
import { t } from "@lingui/core/macro";
import { TaxonomyListProps } from "../../api/customObjectsTypes";
import { ConverterConfigurations } from "./subTaxonomyPages/converterConfigurations";
import { capitalizeKeys } from "../../helpers/capitalizeKeys";
import AlertSnackbar from "../alertSnackbar";

/*  NOT IN USE 
------------------------------------------------------------------------ */

export interface TaxonomyDetailsDialogProps {
    open: boolean;
    taxonomyId: string | undefined;
    onClose: (updateStatus: "none" | "success" | "error", reason?: string) => void;
}

const DISPLAY_TO_BACKEND_MAPPING: Record<string, string> = { "Euro Fillings": "EuroFilling" };

type IstarterPages = "details" | "entrypoints" | "taxonomyelements" | "taxonomyDimensionValues" | "viewer" | "logInformation" | "converterconfigurations";

export const TaxonomyById = ({ starterPage }: { starterPage: IstarterPages }) => {
    const { id } = useParams();
    const location = useLocation();
    const [cacheBuster, setCacheBuster] = useState(crypto.randomUUID());
    const [updating, setUpdating] = useState(false);
    const [permissionScheme, setPermissionScheme] = useState<ApiDetailedTaxonomy["permissionScheme"] | null>(null);
    const [allPermissionSchemes, setAllPermissionSchemes] = useState<ApiSchemes[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [initialData, setInitialData] = useState<{
        type: string;
        systemName: string | undefined;
        permissionScheme: ApiDetailedTaxonomy["permissionScheme"];
        validatingAuthority: string | null;
    }>({
        type: "",
        systemName: "",
        validatingAuthority: null,
        permissionScheme: {
            id: "",
            name: "",
        },
    });

    const [alertMessage, setAlertMessage] = useState<AlertMessage | undefined>(undefined);

    const [taxonomyData, setTaxonomyData] = useState<TaxonomyListProps | null>(null);

    const { data, loading, error, setData } = useGetQuery<ApiDetailedTaxonomy>(
        `/api/taxonomies/${id}?cb=${cacheBuster}`,
        !id
    );

    useEffect(() => {
        if (data) {
            setTotalCount(0);
        }
    }, [data, starterPage]);

    useEffect(() => {
        const fetchTaxonomyConfigurations = async () => {
            try {
                const response = await getCustomObjectTaxonomies();
                const taxonomyConfigurations = response.data;

                if (data?.systemName) {
                    const match = taxonomyConfigurations.find(
                        (item: TaxonomyListProps) => item?.systemName === data?.systemName
                    );
                    setTaxonomyData(match || null);
                }
            } catch (error) {
                console.error("Error fetching taxonomy configurations:", error);
            }
        };

        if (data && !taxonomyData) {
            fetchTaxonomyConfigurations();
        }
    }, [data, taxonomyData]);

    useEffect(() => {
        if (data) {
            setPermissionScheme(data.permissionScheme || null);
            if (!initialData.systemName) {
                setInitialData({
                    type: DISPLAY_TO_BACKEND_MAPPING[data.type] || data.type,
                    systemName: data.systemName,
                    permissionScheme: data.permissionScheme ? data.permissionScheme : { id: "", name: "" },
                    validatingAuthority: data.type === "EuroFilling" ? data.validatingAuthority : null,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (data && !allPermissionSchemes.length) {
            const fetchPermissionSchemes = async () => {
                const response = await getActiveTaxonomySchemes(cacheBuster);
                setAllPermissionSchemes(response.data);
            };
            fetchPermissionSchemes();
        }
    }, [data, cacheBuster, allPermissionSchemes.length]);

    if (location.pathname === `/taxonomies/${id}/`) {
        window.location.href = `/taxonomies/${id}/details`;
    }

    const handleSubmit = async () => {
        setUpdating(true);
        try {
            if (data && starterPage === "details") {
                await updateTaxonomy(id as string, {
                    systemName: data.systemName,
                    permissionScheme: permissionScheme?.id || null,
                    type: data.type,
                    validatingAuthority: data.type === "EuroFilling" && !data.validatingAuthority ? null : data.validatingAuthority || null,
                });
            } else if (starterPage === "converterconfigurations") {
                const capitalizeData = capitalizeKeys(taxonomyData);
                await updateTaxonomyConfiguration(
                    taxonomyData?.id as string,
                    capitalizeData
                );
            }
        } catch (error) {
            console.error("Error updating taxonomy:", error);
        } finally {
            setUpdating(false);
            setCacheBuster(crypto.randomUUID());
        }
    };

    const navBarItems = [
        { key: "details", label: t`Details`, disabled: false },
        { key: "converterconfigurations", label: t`Converter Configurations`, disabled: !taxonomyData },
        { key: "entrypoints", label: t`Entry Points`, disabled: !taxonomyData },
        { key: "taxonomyelements", label: t`Taxonomy Elements`, disabled: !taxonomyData },
        { key: "taxonomydimensionvalues", label: t`Taxonomy Dimension Values`, disabled: !taxonomyData },
    ];

    const tabElements = navBarItems.map((item) => (
        <Tab
            key={item.key}
            component={Link}
            to={`/taxonomies/${id}/${item.key}`}
            value={`/taxonomies/${id}/${item.key}`}
            label={item?.label}
            disabled={item?.disabled}
            style={{ color: item?.disabled ? "#bdbdbd" : theme.palette.primary.main }}
            {...a11yProps(item?.label)}
        />
    ));

    return (
        <>
            <AppBar sx={{ mt: 9, height: 48, zIndex: theme.zIndex.drawer - 1, backgroundColor: "#f8f8f8", display: "flex", justifyContent: "center" }}>
                <Toolbar>
                    <Tabs value={location.pathname.toLowerCase()}>{tabElements}</Tabs>
                </Toolbar>
            </AppBar>
            <ContainerWrapper>
                <Paper sx={{ p: 5, mt: 2, }} elevation={0}>
                    {loading ? (
                        <Box sx={{ minWidth: 500, minHeight: 500 }}><Loader height={500} /></Box>
                    ) : error ? (
                        <Alert severity="error">{t`Could not load taxonomy details. Please try again later`}</Alert>
                    ) : data ? (
                        <>
                            <Grid container justifyContent="space-between" alignItems="center">
                                <Typography variant="h4">
                                    {data.systemName} {" "}
                                    {starterPage === "details" ? t`Taxonomy Details` :
                                        starterPage === "entrypoints" ? t`Taxonomy Entry Points` :
                                            starterPage === "taxonomyelements" ? t`Taxonomy Elements` :
                                                starterPage === "taxonomyDimensionValues" ? t`Taxonomy Dimension Values` :
                                                    starterPage === "converterconfigurations" ? t`Converter Configurations` : ""
                                    }
                                </Typography>
                                {(starterPage === "taxonomyelements" || starterPage === "taxonomyDimensionValues") && totalCount > 0 && (
                                    <Typography
                                        sx={{
                                            display: "inline-block",
                                            pb: 0.5,
                                            pt: 0.5,
                                            pl: 1,
                                            pr: 4,
                                            borderRadius: 2,
                                        }}
                                    >
                                        {t`Total:`} {totalCount}
                                    </Typography>
                                )}
                            </Grid>

                            {starterPage === "details" && (
                                <TaxonomyDetails
                                    taxonomyData={data}
                                    setData={setData}
                                    taxonomySchemes={allPermissionSchemes}
                                    permissionScheme={permissionScheme}
                                    setPermissionScheme={setPermissionScheme}
                                />
                            )}
                            {starterPage === "converterconfigurations" && (
                                <ConverterConfigurations
                                    taxonomyData={taxonomyData}
                                    setTaxonomyData={setTaxonomyData}
                                />
                            )}
                            {starterPage === "entrypoints" ?
                                <TaxonomyEntryPoints
                                    id={taxonomyData?.id || ""}
                                    setAlertMessage={setAlertMessage}
                                /> : null}
                            {starterPage === "taxonomyelements" && taxonomyData ?
                                <TaxonomyElements
                                    id={taxonomyData?.id || ""}
                                    setAlertMessage={setAlertMessage}
                                    setTotal={setTotalCount}
                                />
                                : null}
                            {starterPage === "taxonomyDimensionValues" && taxonomyData ?
                                <TaxonomyDimensionValues
                                    id={taxonomyData?.id || ""}
                                    setAlertMessage={setAlertMessage}
                                    setTotal={setTotalCount}
                                />
                                : null}

                            {(starterPage === "details" || starterPage === "converterconfigurations") && (
                                <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                                    <Button variant="contained" onClick={handleSubmit}
                                        disabled={updating}>
                                        {updating ? <CircularProgress size={25} /> : "Update"}
                                    </Button>
                                </Box>
                            )}
                        </>
                    ) : null}
                </Paper>
                <AlertSnackbar
                    alertMessage={alertMessage}
                    updateAlertStatus={setAlertMessage}
                    duration={3000}
                />
            </ContainerWrapper>
        </>
    );
};

function a11yProps(value: string) {
    return { id: `simple-tab-${value}`, "aria-controls": `simple-tabpanel-${value}` };
}

export default TaxonomyById;
